/**
 * Godot's editor preview lighting (ADR-0025): a scene with no sun gets the preview
 * sun and one with no environment the preview environment, each yielding on its
 * own. At runtime Godot adds neither. Values from `Node3DEditor::_load_default_preview_settings`
 * and `_preview_settings_changed`, the yield rule from `_node_added`.
 */
import * as THREE from 'three';
import { createEnvironmentSettings } from '../../resources/environment/build';
import { DEFAULT_CLEAR_COLOR } from '../../godot/rendering/rendering';
import { lerpColor } from '../../godot/math/math';
import { decodeSky, decodeSkyMaterial } from '../../resources/sky/decode';
import { decodeEnvironment } from '../../resources/environment/decode';
import { degToRad } from '../../godot/math/math.js';
import { BackgroundMode } from '../../godot/scene/environment.js';
import { ToneMapper } from '../../godot/rendering/environment/toneMapping.js';
/** The node types the editor counts, one independent counter each. */
export const PREVIEW_SUN_YIELD_TYPE = 'DirectionalLight3D';
export const PREVIEW_ENVIRONMENT_YIELD_TYPE = 'WorldEnvironment';
/**
 * "These default rotations place the preview sun at an angular altitude of 60
 * degrees (must be negative) and an azimuth of 30 degrees clockwise from north
 * (or 150 CCW from south), from north east, facing south west."
 */
const PREVIEW_SUN_ALTITUDE_DEG = -60;
const PREVIEW_SUN_AZIMUTH_DEG = 150;
/** Godot's preview sun is white at energy 1.0 with shadows enabled. */
export const PREVIEW_SUN_COLOR = 0xffffff;
export const PREVIEW_SUN_ENERGY = 1;
export const PREVIEW_SUN_SHADOW_MAX_DISTANCE = 100;
const PREVIEW_SKY_TOP = { r: 0.385, g: 0.454, b: 0.55, a: 1 };
const PREVIEW_GROUND_BOTTOM = { r: 0.2, g: 0.169, b: 0.133, a: 1 };
/**
 * The nodes Godot's two counters watch. A stable module-level predicate: the
 * live-tree hook memoises on it.
 */
export const YIELDS_A_PREVIEW = (node) => node.type === PREVIEW_SUN_YIELD_TYPE || node.type === PREVIEW_ENVIRONMENT_YIELD_TYPE;
/**
 * Which previews to mount. Mirrors `_node_added`: by node type only, so a hidden
 * `DirectionalLight3D` with zero energy still takes the preview sun away.
 */
export function previewYield(sceneNodeTypes, toggles) {
    let hasDirectionalLight = false;
    let hasWorldEnvironment = false;
    for (const type of sceneNodeTypes) {
        if (type === PREVIEW_ENVIRONMENT_YIELD_TYPE)
            hasWorldEnvironment = true;
        else if (type === PREVIEW_SUN_YIELD_TYPE)
            hasDirectionalLight = true;
    }
    return {
        sun: toggles.sun && !hasDirectionalLight,
        environment: toggles.environment && !hasWorldEnvironment,
    };
}
/**
 * The direction the preview sun's light travels: Godot's euler applied to a
 * light's local -Z, in the YXZ order `Basis::from_euler` defaults to, which three
 * spells the same way.
 */
export function previewSunDirection() {
    const euler = new THREE.Euler(degToRad(PREVIEW_SUN_ALTITUDE_DEG), degToRad(PREVIEW_SUN_AZIMUTH_DEG), 0, 'YXZ');
    return new THREE.Vector3(0, 0, -1).applyEuler(euler).normalize();
}
/** Godot's `Color::get_luminance()`, with Rec. 709 coefficients. */
function luminance({ r, g, b }) {
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/**
 * The preview horizon is not the midpoint of sky and ground: Godot takes that
 * midpoint, computes its luminance scaled by 3.333, and pushes the colour
 * halfway towards that grey. The push is what keeps the horizon bright rather
 * than muddy.
 */
function previewHorizonColor() {
    const midpoint = lerpColor(PREVIEW_SKY_TOP, PREVIEW_GROUND_BOTTOM, 0.5);
    const lum = luminance(midpoint) * 3.333;
    return lerpColor(midpoint, { r: lum, g: lum, b: lum, a: 1 }, 0.5);
}
/**
 * Godot's preview environment, through the same decode and build pipeline as an
 * authored `WorldEnvironment`, so the two cannot diverge. Its glow runs in the
 * tonemap pass (`toneMapPass.ts`). Built once: the root's layer and a pass that draws it read one
 * object, so the pass publishes it once. The preview environment and its sky hold no references.
 */
export function previewEnvironment() {
    return (builtPreviewEnvironment ??= buildPreviewEnvironment());
}
/** Written only by `previewEnvironment`. Never cleared, since the preview never changes. */
let builtPreviewEnvironment;
function buildPreviewEnvironment() {
    const settings = createEnvironmentSettings(decodeEnvironment({
        background_mode: String(BackgroundMode.BG_SKY),
        // The preview departs from an authored Environment's LINEAR default.
        tonemap_mode: String(ToneMapper.FILMIC),
        // The editor preview enables glow; emissive materials bloom because of it.
        glow_enabled: 'true',
    }), 
    // BG_SKY draws no clear colour.
    DEFAULT_CLEAR_COLOR);
    const horizon = previewHorizonColor();
    const material = decodeSkyMaterial('ProceduralSkyMaterial', {});
    return {
        settings,
        panorama: null,
        glowMap: null,
        // The editor's Sky keeps every default but its material (`node_3d_editor_plugin.cpp:10394`).
        sky: decodeSky({}, {
            ...material,
            sky_top_color: PREVIEW_SKY_TOP,
            sky_horizon_color: horizon,
            ground_bottom_color: PREVIEW_GROUND_BOTTOM,
            ground_horizon_color: horizon,
        }),
    };
}
//# sourceMappingURL=godotPreviewLighting.js.map